import { Divider, type TablePaginationConfig } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import i18n from "i18next";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router";

import { DealerChangeButton } from "src/app/features/Site/components/buttons/DealerChangeButton";
import { defaultMapperKey } from "../../core/mappers/mappers";
import { getAssignedDataGroup } from "../../core/router/auth";
import { canAccess } from "../../core/router/auth/permissions";
import { Permissions } from "../../core/router/config/Permissions";
import type { ResourceName } from "../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../core/router/types/routerTypes";
import { showInfoNotification, showSuccessNotification } from "../../core/setup/redux/state/notifications/notificationsState";
import type { Entity } from "../../core/types/fe";
import { ActionBar } from "../ActionBar/ActionBar";
import { AddButton } from "../FeatureButtons/AddButton";
import { CopyButton } from "../FeatureButtons/CopyButton";
import { CSVImportButton } from "../FeatureButtons/CSVImportButton";
import { DeleteButton } from "../FeatureButtons/DeleteButton";
import { ExportButton } from "../FeatureButtons/ExportButton";
import { FormCopyButton } from "../FeatureButtons/FormCopyButton";
import { ImportButton } from "../FeatureButtons/ImportButton";
import type { AlertActionPayload, ExtendedAlertActionPayload } from "../NotificationAlerts/types";
import {
    createQueryParams,
    loadPersistedQueryConfig,
    persistQueryConfig,
    type QueryParamsConfig,
    useExportTableDataMutation,
    useGetTableDataQuery,
    useUpdateTableDataMutation,
} from "../Table";
import { ResourceManagerWrapper } from "./ResourceManagerTableStyles";

/**
 * Default page size and page number on tables that uses pagination.
 */
export const DEFAULT_PAGE_SIZE = 20;
export const DEFAULT_PAGE_NUMBER = 1;

export enum Actions {
    ADD,
    COPY,
    FORM_COPY,
    DEALER_CHANGE,
    DELETE,
    EXPORT,
    IMPORT,
    IMPORT_DATA,
}

type CustomActionType = {
    action: React.ReactElement;
    permission?: string;
};

type ResourceManagerProps<T extends Entity = Entity> = {
    useDataGroup: boolean; // adds data group at the beginning of the request url
    dataMapperKey?: string; // the name of the mapper used to shape the dataset
    table: React.JSX.Element; // the resource name or composed url from fetch and to send BE data.
    resource: ResourceName | string; // the table component to be used to display the data
    actions?: Actions[]; // set the common table actions such Add, Delete, Copy, ...
    pollingInterval?: number; // if set, auto-refreshes the table at this interval in milliseconds
    actionCallbacks?: {
        // allows to introduce new action buttons
        add?: () => void;
        batchAdd?: () => void;
        copy?: (item: Entity) => void;
        delete?: () => void;
        export?: () => void;
        formCopy?: (item: Entity) => void;
        import?: () => void;
    };
    customActions?: CustomActionType[]; // if defined, override default action callbacks.
    searchForm?: React.JSX.Element; // if used, it filters the table data by calling an endpoint
    isLoading?: boolean;
    shouldShowCopyAction?: (selectedItem: T | undefined, actionType: Actions.COPY | Actions.FORM_COPY) => boolean; // conditional rendering of copy actions
    getCopyRedirectUrl?: (copiedItem: Entity, resource: ResourceName | string) => string; // custom redirect URL after copy success
};

export const ResourceManager = <T extends Entity>({
    useDataGroup,
    dataMapperKey,
    resource,
    table,
    actions,
    pollingInterval,
    customActions,
    actionCallbacks,
    searchForm,
    isLoading,
    shouldShowCopyAction,
    getCopyRedirectUrl,
}: ResourceManagerProps) => {
    const groupCode = getAssignedDataGroup()?.code;
    const baseURI = useDataGroup ? `data-groups/${groupCode}` : "";

    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const resourceId = selectedRows?.length ? selectedRows[0].id : "";
    const isOneSelected = selectedRows.length === 1;

    const isDisabledDelete = !isOneSelected;
    const isDisabledCopy = !isOneSelected;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const { t } = useTranslation();

    const notifyOkDelete = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formDeleteSuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const notifyOkCopy = useCallback(
        (payload: ExtendedAlertActionPayload) => {
            dispatch(
                showSuccessNotification({
                    text: i18n.t("common.formCopySuccess", {
                        id: payload.id,
                        resource: payload.name,
                    }),
                    autoCloseDisabled: false,
                }),
            );
        },
        [dispatch],
    );

    const notifyInfo = useCallback(() => {
        dispatch(
            showInfoNotification({
                text: t("info.export"),
                autoCloseDisabled: false,
            }),
        );
    }, [dispatch, t]);

    const [query, setQuery] = useState<string>("");
    const [queryConfig, setQueryConfig] = useState<QueryParamsConfig<T>>(
        () =>
            loadPersistedQueryConfig<T>(pathname) ?? {
                pageSize: DEFAULT_PAGE_SIZE,
                currentPage: DEFAULT_PAGE_NUMBER,
                filters: {} as T,
                sorter: [],
            },
    );

    const resourceFetchPath = `${baseURI}/${resource}`;
    const mapperKey = dataMapperKey || defaultMapperKey;
    const { data, isFetching, refetch } = useGetTableDataQuery(
        {
            resourceFetchPath,
            query,
            dataMapperKey: mapperKey,
        },
        { skip: query === "", pollingInterval },
    );

    // public reload for children
    // reload the table when a custom action is performed e.g. generateCampaign
    // selection is cleared because the selected rows hold stale data (e.g. state) that drives which actions are enabled
    const onReload = () => {
        setSelectedRows([]);
        refetch();
    };

    useEffect(() => {
        const newQuery = createQueryParams(queryConfig);
        setQuery(newQuery);
        persistQueryConfig(pathname, queryConfig);
    }, [queryConfig, pathname]);

    const onRowSelectionChange = useCallback(
        (rows: T[]) => {
            setSelectedRows(rows);
        },
        [setSelectedRows],
    );

    const onStateChanged = useCallback(
        (newSorter: SorterResult<T> | SorterResult<T>[], newPagination: TablePaginationConfig) => {
            setQueryConfig({
                ...queryConfig,
                sorter: newSorter,
                pageSize: newPagination.pageSize,
                currentPage: newPagination.current,
            });
        },
        [queryConfig],
    );

    const onSearch = (filters: T) => {
        setQueryConfig({ ...queryConfig, filters, currentPage: 1 });
    };

    const [updateTable, { isLoading: isUpdating }] = useUpdateTableDataMutation();
    const backendCopy = useCallback(() => {
        updateTable({
            url: `${baseURI}/${resource}/${resourceId}/${FORM_ACTION.COPY}`,
            method: "POST",
        })
            .unwrap()
            .then((payload) => performCopySuccess(payload))
            .catch(() => {
                // error handled in rtkQueryErrorLogger
            });

        const performCopySuccess = (payload: AlertActionPayload | undefined) => {
            setSelectedRows([]);
            const payloadIdObject = payload as Entity;
            const redirectUrl = getCopyRedirectUrl
                ? getCopyRedirectUrl(payloadIdObject, resource)
                : `${BASE_PATH}/${resource}/${FORM_ACTION.EDIT}/${payloadIdObject.id}`;
            navigate(redirectUrl);
            if (payload) {
                notifyOkCopy(payload);
            }
        };
    }, [updateTable, baseURI, resource, resourceId, navigate, notifyOkCopy, getCopyRedirectUrl]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const performDeleteSuccess = (payload: AlertActionPayload | undefined) => {
        setSelectedRows([]);
        setQueryConfig({ ...queryConfig, currentPage: 1 });
        if (payload) {
            notifyOkDelete(payload);
        }
    };

    const backendDelete = useCallback(() => {
        updateTable({
            url: `${baseURI}/${resource}/${resourceId}`,
            method: "DELETE",
        })
            .unwrap()
            .then((payload) => performDeleteSuccess(payload))
            .catch(() => {
                /* empty - error notification is shown and handled in rtkQueryErrorLogger */
            });
    }, [updateTable, baseURI, resource, resourceId, performDeleteSuccess]);

    const [exportRequest, { error: exportError }] = useExportTableDataMutation();
    const backendExport = useCallback(() => {
        // Reuse table filters for export, but strip sorting/pagination params.
        const exportParams = new URLSearchParams(query);
        exportParams.delete("orderBy");
        exportParams.delete("pageSize");
        exportParams.delete("pageNumber");
        exportParams.set("format", "csv");

        exportRequest({
            url: `${baseURI}/${resource}/export?${exportParams.toString()}`,
            method: "GET",
            downloadFileName: String(resource),
        });

        // notify info outside of export request to show immediately
        if (!exportError) {
            notifyInfo();
        }
    }, [baseURI, exportError, exportRequest, notifyInfo, resource, query]);

    const formCopy = useCallback(() => {
        navigate(`${BASE_PATH}/${resource}/${FORM_ACTION.COPY}/${resourceId}`);
    }, [navigate, resource, resourceId]);

    // ACTION ACCESS CONFIGURATION
    const canAdd = canAccess(resource, Permissions.ADD) && actions?.some((action) => action === Actions.ADD);
    const canDelete = canAccess(resource, Permissions.DELETE) && actions?.some((action) => action === Actions.DELETE);
    const canCopy = canAccess(resource, Permissions.COPY) && actions?.some((action) => action === Actions.COPY);
    const canFormCopy = canAccess(resource, Permissions.COPY) && actions?.some((action) => action === Actions.FORM_COPY);
    const canDealerChange = canAccess(resource, Permissions.DEALER_CHANGE) && actions?.some((action) => action === Actions.DEALER_CHANGE);
    const canExport = canAccess(resource, Permissions.EXPORT) && actions?.some((action) => action === Actions.EXPORT);
    const canImport = canAccess(resource, Permissions.IMPORT) && actions?.some((action) => action === Actions.IMPORT);
    const canImportData = canAccess(resource, Permissions.IMPORT) && actions?.some((action) => action === Actions.IMPORT_DATA);

    const fetchingData = isLoading || isFetching || isUpdating || false;
    const [datasource, setDatasource] = useState({
        data: (data?.list as T[]) || [],
        pageNumber: queryConfig.currentPage,
        pageSize: queryConfig.pageSize,
        totalPages: data?.totalPages,
        totalNumberOfResults: data?.totalNumberOfResults,
        isFetching: fetchingData,
    });
    useEffect(() => {
        setDatasource({
            data: (data?.list as T[]) || [],
            pageNumber: queryConfig.currentPage,
            pageSize: queryConfig.pageSize,
            totalPages: data?.totalPages,
            totalNumberOfResults: data?.totalNumberOfResults,
            isFetching: fetchingData,
        });
    }, [fetchingData, data, queryConfig]);

    return (
        <ResourceManagerWrapper>
            <Divider />
            {searchForm && React.cloneElement(searchForm, { ...searchForm.props, searchCallback: onSearch })}
            <ActionBar className="left">
                {canAdd && <AddButton callback={actionCallbacks?.add} resourceName={resource} isDisabled={false} />}
                {canImportData && <CSVImportButton isDisabled={false} resourceName={resource} />}
                {canDelete && (
                    <DeleteButton
                        itemToDelete={selectedRows[0]}
                        isDisabled={isDisabledDelete}
                        callback={actionCallbacks?.delete || backendDelete}
                    />
                )}
                {canExport && <ExportButton callback={actionCallbacks?.export || backendExport} />}
                {canImport && <ImportButton resourceName={resource} isDisabled={false} callback={actionCallbacks?.import} />}
                {canCopy && (shouldShowCopyAction?.(selectedRows[0], Actions.COPY) ?? true) && (
                    <CopyButton isDisabled={isDisabledCopy} callback={actionCallbacks?.copy || backendCopy} itemToCopy={selectedRows[0]} />
                )}
                {canFormCopy && (shouldShowCopyAction?.(selectedRows[0], Actions.FORM_COPY) ?? true) && (
                    <FormCopyButton
                        isDisabled={isDisabledCopy}
                        onClick={actionCallbacks?.formCopy || formCopy}
                        itemToCopy={selectedRows[0]}
                    />
                )}
                {canDealerChange && (
                    <DealerChangeButton isDisabled={isDisabledDelete} item={selectedRows[0]} onFinish={() => setSelectedRows([])} />
                )}
                {customActions?.map(({ action, permission }) => {
                    const hasAccess = permission ? canAccess(resource, permission) : true; // Default to true if no permission is specified

                    return hasAccess
                        ? React.cloneElement(action, {
                              // @ts-ignore
                              ...action.props,
                              item: selectedRows.length > 0 ? selectedRows[0] : undefined,
                              items: selectedRows,
                              datasource,
                              onRowSelectionChange,
                              onReload,
                          })
                        : null;
                })}
            </ActionBar>
            {React.cloneElement(table, {
                ...table.props,
                resource,
                datasource,
                onRowSelectionChange,
                onStateChanged,
                selectedRowKeys: selectedRows.map((row) => row.id),
            })}
        </ResourceManagerWrapper>
    );
};
